import { supabaseClient } from '@/lib/supabaseClient';
import { clearCartToken, ensureCartToken } from '@/lib/cartSession';

// ✅ Clave única y consistente en todo el proyecto
const CART_SESSION_KEY = 'cart_session_id';

// Claves antiguas a migrar
const LEGACY_KEYS = ['sol_sazon_session_id', 'cart_session', 'session_id'];
let pendingSession: Promise<string> | null = null;

/**
 * Migra claves antiguas a la nueva clave estándar
 */
function migrateLegacySessionId(): string | null {
  for (const oldKey of LEGACY_KEYS) {
    const oldValue = localStorage.getItem(oldKey);
    if (oldValue) {
      localStorage.setItem(CART_SESSION_KEY, oldValue);
      localStorage.removeItem(oldKey);
      return oldValue;
    }
  }
  return null;
}

/**
 * Obtiene o crea el session_id del carrito.
 * - Genera UUID real con crypto.randomUUID()
 * - Persiste en localStorage y en public.cart_sessions
 */
export async function getOrCreateSessionId(): Promise<string> {
  if (pendingSession) return pendingSession;
  pendingSession = (async () => {
    let sessionId = getCurrentSessionId() || crypto.randomUUID();
    ensureCartToken();
    if (!supabaseClient) {
      localStorage.setItem(CART_SESSION_KEY, sessionId);
      return sessionId;
    }
    for (let attempt = 0; attempt < 2; attempt++) {
      const { data, error } = await supabaseClient.rpc('ensure_cart_session', { p_session_id: sessionId });
      if (!error && data?.id && !data.converted) {
        localStorage.setItem(CART_SESSION_KEY, data.id);
        return data.id as string;
      }
      if (error && error.code !== '42501') throw new Error(error.message || 'No se pudo abrir el carrito');
      if (error?.code === '42501') window.dispatchEvent(new Event('cart-session-renewed'));
      // A formerly public guest ID cannot be reclaimed without its capability.
      // Existing database rows are retained; only this browser's unproven reference rotates.
      clearSessionId();
      ensureCartToken();
      sessionId = crypto.randomUUID();
    }
    throw new Error('No se pudo abrir una sesión segura del carrito');
  })();
  try { return await pendingSession; }
  finally { pendingSession = null; }
}

/**
 * Obtiene el session_id actual sin crear uno nuevo
 */
export function getCurrentSessionId(): string | null {
  // Primero intentar migrar
  migrateLegacySessionId();
  return localStorage.getItem(CART_SESSION_KEY);
}

/**
 * Limpia el session_id local (tras checkout o clear)
 */
export function clearSessionId(): void {
  localStorage.removeItem(CART_SESSION_KEY);
  clearCartToken();
  // Limpiar también claves antiguas por si acaso
  LEGACY_KEYS.forEach(key => localStorage.removeItem(key));
}

export interface CartItemDB {
  id: string;
  session_id: string;
  variant_id: string;
  quantity: number;
  // Datos del JOIN
  product_id: number;
  product_name: string;
  product_image: string;
  product_category: string;
  variant_label: string;
  variant_price: number;
  variant_stock: number;
}

/* ------------------------------------------------------------------ */
/*  cart_view unified row                                              */
/* ------------------------------------------------------------------ */

export interface CartViewRow {
  id: string;
  session_id: string;
  user_id: string | null;
  item_type: 'product' | 'bundle';
  // Bundle fields (null for products)
  bundle_id: number | null;
  bundle_slug: string | null;
  bundle_name: string | null;
  bundle_image: string | null;
  bundle_price: number | null;
  // Product fields (null for bundles)
  product_id: number | null;
  product_name: string | null;
  product_image: string | null;
  product_category: string | null;
  variant_id: string | null;
  variant_label: string | null;
  variant_price: number | null;
  variant_stock: number | null;
  product_original_price: number | null;
  // Snapshot price fields (from cart_items_priced via cart_view)
  unit_price: number | null;
  original_unit_price: number | null;
  price_locked_at: string | null;
  line_total: number | null;
  // Common
  quantity: number;
  created_at: string;
}

/**
 * Carga el carrito unificado desde la vista cart_view
 */
export async function fetchCartView(sessionId: string): Promise<CartViewRow[]> {
  if (!supabaseClient) return [];

  const { data, error } = await supabaseClient
    .from('cart_view')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[CartService] Error cargando cart_view:', error);
    return [];
  }

  if (!data) return [];

  return (data as any[]).map((row) => ({
    id: row.id,
    session_id: row.session_id,
    user_id: row.user_id,
    item_type: row.item_type as 'product' | 'bundle',
    bundle_id: row.bundle_id,
    bundle_slug: row.bundle_slug,
    bundle_name: row.bundle_name,
    bundle_image: row.bundle_image,
    bundle_price: row.bundle_price != null ? Number(row.bundle_price) : null,
    product_id: row.product_id,
    product_name: row.product_name,
    product_image: row.product_image,
    product_category: row.product_category,
    variant_id: row.variant_id,
    variant_label: row.variant_label,
    variant_price: row.variant_price != null ? Number(row.variant_price) : null,
    variant_stock: row.variant_stock,
    product_original_price: row.product_original_price != null ? Number(row.product_original_price) : null,
    unit_price: row.unit_price != null ? Number(row.unit_price) : null,
    original_unit_price: row.original_unit_price != null ? Number(row.original_unit_price) : null,
    price_locked_at: row.price_locked_at || null,
    line_total: row.line_total != null ? Number(row.line_total) : null,
    quantity: row.quantity,
    created_at: row.created_at,
  }));
}

/**
 * Carga los items del carrito desde BD con datos de producto y variante
 */
export async function fetchCartItems(sessionId: string): Promise<CartItemDB[]> {
  if (!supabaseClient) return [];

  const { data, error } = await supabaseClient
    .from('cart_items')
    .select(`
      id,
      session_id,
      variant_id,
      quantity,
      product_variants (
        id,
        product_id,
        label,
        price,
        stock,
        products (
          id,
          name,
          image,
          images,
          category
        )
      )
    `)
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[CartService] Error cargando cart_items:', error);
    return [];
  }

  if (!data) return [];

  return data
    .filter((row: any) => row.product_variants)
    .map((row: any) => {
      const variant = row.product_variants;
      const product = variant.products;
      if (!product) return null;

      const productImage = (product.images && Array.isArray(product.images) && product.images.length > 0 && product.images[0])
        ? product.images[0]
        : (product.image || '');

      return {
        id: row.id,
        session_id: row.session_id,
        variant_id: row.variant_id,
        quantity: row.quantity,
        product_id: variant.product_id,
        product_name: product.name,
        product_image: productImage,
        product_category: product.category || '',
        variant_label: variant.label,
        variant_price: Number(variant.price),
        variant_stock: variant.stock,
      };
    })
    .filter(Boolean) as CartItemDB[];
}

/**
 * Añade un producto al carrito usando la RPC add_to_cart (UPSERT nativo)
 */
export async function addItemToCart(
  sessionId: string,
  variantId: string,
  qty: number,
  userId?: string | null
): Promise<void> {
  if (!supabaseClient) throw new Error('Supabase no configurado');

  // Validar que tenemos un variantId válido
  if (!variantId || variantId.trim() === '') {
    throw new Error('No se ha seleccionado una variante válida');
  }

  const { data: _data, error } = await supabaseClient.rpc('add_to_cart', {
    p_session_id: sessionId,
    p_user_id: userId || null,
    p_variant_id: variantId,
    p_qty: qty,
  });

  if (error) {
    if (error.message?.includes('Variante no disponible')) {
      throw new Error('Este producto no está disponible o no tiene stock suficiente');
    }
    if (error.message?.includes('cantidad debe ser mayor')) {
      throw new Error('La cantidad debe ser mayor que 0');
    }
    throw new Error(error.message || 'No se pudo añadir al carrito');
  }
}

/**
 * Actualiza la cantidad de un item del carrito
 */
export async function updateItemQuantity(itemId: string, newQuantity: number): Promise<void> {
  if (!supabaseClient) return;

  const { error } = await supabaseClient
    .from('cart_items')
    .update({ quantity: newQuantity, updated_at: new Date().toISOString() })
    .eq('id', itemId);

  if (error) {
    console.error('[CartService] Error actualizando cantidad:', error);
    throw new Error('No se pudo actualizar la cantidad');
  }
}

/**
 * Elimina un item del carrito
 */
export async function removeItemFromCart(itemId: string): Promise<void> {
  if (!supabaseClient) return;

  const { error } = await supabaseClient
    .from('cart_items')
    .delete()
    .eq('id', itemId);

  if (error) {
    console.error('[CartService] Error eliminando item:', error);
    throw new Error('No se pudo eliminar el item');
  }
}

/**
 * Actualiza la cantidad de un bundle en cart_bundles directamente (sin RPC)
 * Preserva el unit_price existente para no perder el precio snapshot
 */
export async function updateBundleQuantityRPC(
  sessionId: string,
  bundleSlug: string,
  delta: number,
  _userId?: string | null,
): Promise<void> {
  if (!supabaseClient) throw new Error('Supabase no configurado');

  // Resolver slug (redireccionamientos)
  const { data: redirectRow } = await supabaseClient
    .from('bundle_slug_redirects')
    .select('to_slug')
    .eq('from_slug', bundleSlug)
    .maybeSingle();

  const resolvedSlug = redirectRow?.to_slug ?? bundleSlug;

  // Obtener bundle_id
  const { data: bundleRow, error: bundleErr } = await supabaseClient
    .from('bundles')
    .select('id, bundle_price, original_price, name')
    .eq('slug', resolvedSlug)
    .maybeSingle();

  if (bundleErr || !bundleRow) {
    throw new Error('No se encontró el pack');
  }

  const bundleId: number = bundleRow.id;

  // Buscar la línea actual en cart_bundles
  const { data: existing, error: fetchErr } = await supabaseClient
    .from('cart_bundles')
    .select('id, quantity, unit_price')
    .eq('session_id', sessionId)
    .eq('bundle_id', bundleId)
    .maybeSingle();

  if (fetchErr || !existing) {
    throw new Error('No se encontró el pack en el carrito');
  }

  const newQty = existing.quantity + delta;

  if (newQty <= 0) {
    // Eliminar la línea
    const { error: delErr } = await supabaseClient
      .from('cart_bundles')
      .delete()
      .eq('id', existing.id);

    if (delErr) throw new Error('No se pudo eliminar el pack del carrito');
    return;
  }

  // Recuperar unit_price existente o recalcular si es NULL
  let unit_price: number | null = existing.unit_price != null ? Number(existing.unit_price) : null;

  if (!unit_price || unit_price <= 0) {
    // Intentar desde packs_pricing_final
    const { data: pricingRow } = await supabaseClient
      .from('packs_pricing_final')
      .select('base_price, promo_price')
      .eq('name', bundleRow.name)
      .maybeSingle();

    if (pricingRow) {
      const promo = pricingRow.promo_price != null ? Number(pricingRow.promo_price) : null;
      const base  = pricingRow.base_price  != null ? Number(pricingRow.base_price)  : null;
      unit_price = promo ?? base ?? null;
    }

    // Fallback: bundles.bundle_price
    if (!unit_price || unit_price <= 0) {
      const fallback = Number(bundleRow.bundle_price ?? bundleRow.original_price ?? 0);
      if (fallback > 0) unit_price = fallback;
    }
  }

  if (!unit_price || unit_price <= 0) {
    throw new Error('Pack sin precio');
  }

  // Actualizar cantidad y asegurar unit_price correcto
  const { error: updateErr } = await supabaseClient
    .from('cart_bundles')
    .update({
      quantity: newQty,
      unit_price,
      updated_at: new Date().toISOString(),
    })
    .eq('id', existing.id);

  if (updateErr) {
    throw new Error(updateErr.message || 'No se pudo actualizar el pack');
  }
}

/**
 * Elimina un bundle del carrito directamente por su id en cart_bundles
 */
export async function removeBundleFromCart(cartBundleId: string): Promise<void> {
  if (!supabaseClient) return;

  const { error } = await supabaseClient
    .from('cart_bundles')
    .delete()
    .eq('id', cartBundleId);

  if (error) {
    console.error('[CartService] Error eliminando bundle del carrito:', error);
    throw new Error('No se pudo eliminar el pack');
  }
}

/**
 * Vacía todos los items de una sesión (productos + bundles)
 */
export async function clearCartItems(sessionId: string): Promise<void> {
  if (!supabaseClient) return;

  // Borrar productos
  const { error: errItems } = await supabaseClient
    .from('cart_items')
    .delete()
    .eq('session_id', sessionId);

  if (errItems) {
    console.error('[CartService] Error vaciando cart_items:', errItems);
  }

  // Borrar bundles
  const { error: errBundles } = await supabaseClient
    .from('cart_bundles')
    .delete()
    .eq('session_id', sessionId);

  if (errBundles) {
    console.error('[CartService] Error vaciando cart_bundles:', errBundles);
  }

  clearSessionId();
}

/**
 * Obtiene la primera variante activa de un producto (para cuando no se especifica variante)
 */
export async function getDefaultVariant(productId: number): Promise<{ id: string; price: number; label: string } | null> {
  if (!supabaseClient) return null;

  const { data, error } = await supabaseClient
    .from('product_variants')
    .select('id, price, label')
    .eq('product_id', productId)
    .eq('is_active', true)
    .order('position', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    console.error('[CartService] Error obteniendo variante por defecto:', error);
    return null;
  }

  return { id: data.id, price: Number(data.price), label: data.label };
}

/**
 * Obtiene todas las variantes activas de un producto
 */
export async function getProductVariants(productId: number): Promise<{
  id: string;
  label: string;
  price: number;
  stock: number;
  weight_g: number | null;
  position: number;
}[]> {
  if (!supabaseClient) return [];

  const { data, error } = await supabaseClient
    .from('product_variants')
    .select('id, label, price, stock, weight_g, position')
    .eq('product_id', productId)
    .eq('is_active', true)
    .order('position', { ascending: true });

  if (error || !data) {
    console.error('[CartService] Error obteniendo variantes:', error);
    return [];
  }

  return data.map((v: any) => ({
    id: v.id,
    label: v.label,
    price: Number(v.price),
    stock: v.stock ?? 0,
    weight_g: v.weight_g,
    position: v.position ?? 0,
  }));
}

/**
 * Cambia la variante de un item del carrito.
 * Si ya existe un item con la nueva variante en la misma sesión, fusiona las cantidades.
 */
export async function changeCartItemVariant(
  itemId: string,
  sessionId: string,
  newVariantId: string,
  _currentQuantity: number,
): Promise<void> {
  if (!supabaseClient) throw new Error('Supabase no configurado');
  const { error } = await supabaseClient.rpc('change_cart_item_variant', {
    p_session_id: sessionId, p_item_id: itemId, p_variant_id: newVariantId,
  });
  if (error) throw new Error(error.message || 'No se pudo cambiar la variante');
}

/**
 * Crea un pedido desde el carrito usando la RPC create_order_from_cart
 */
export async function createOrderFromCart(params: {
  sessionId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  province: string;
  discountTotal?: number;
  taxTotal?: number;
  shippingCost?: number;
  currency?: string;
  userId?: string | null;
  shippingMethodId?: string | null;
  shippingZoneName?: string | null;
  loyaltyPoints?: number;
  couponCode?: string | null;
}): Promise<{
  orderId: string;
  orderNumber: string;
  subtotal: number;
  discount: number;
  shippingCost: number;
  taxTotal: number;
  total: number;
  replayed: boolean;
}> {
  if (!supabaseClient) throw new Error('Supabase no configurado');

  const { data, error } = await supabaseClient.rpc('create_order_from_cart', {
    p_session_id: params.sessionId,
    p_email: params.email,
    p_first_name: params.firstName,
    p_last_name: params.lastName,
    p_phone: params.phone,
    p_address: params.address,
    p_city: params.city,
    p_postal_code: params.postalCode,
    p_province: params.province,
    p_shipping_method_id: params.shippingMethodId ?? null,
    p_shipping_zone_name: params.shippingZoneName ?? null,
    p_loyalty_points: Math.max(0, Math.trunc(params.loyaltyPoints ?? 0)),
    p_coupon_code: params.couponCode?.trim() || null,
    // Parámetros históricos conservados por compatibilidad; el servidor los ignora.
    p_discount_total: 0,
    p_tax_total: 0,
    p_shipping_cost: 0,
    p_currency: 'EUR',
    p_user_id: null,
  });

  if (error) {
    console.error('[CartService] Error en create_order_from_cart:', error);
    throw new Error(error.message || 'No se pudo crear el pedido');
  }

  const result = data as Record<string, unknown> | null;
  const orderId = typeof result?.order_id === 'string' ? result.order_id : '';
  if (!orderId) throw new Error('La respuesta del servidor no contiene un pedido válido');

  // La RPC marca la sesión como convertida y guarda método/zona dentro de la
  // misma transacción que pedido, líneas, cupón, puntos y stock.
  clearSessionId();
  return {
    orderId,
    orderNumber: String(result?.order_number ?? ''),
    subtotal: Number(result?.subtotal ?? 0),
    discount: Number(result?.discount ?? 0),
    shippingCost: Number(result?.shipping_cost ?? 0),
    taxTotal: Number(result?.tax_total ?? 0),
    total: Number(result?.total ?? 0),
    replayed: result?.replayed === true,
  };
}

